// ============================================================================
// The two things a person can do to a row on Home and My Work (item #835,
// Ben 2026-10-13, clearing option B). Both use doors that already exist — no
// new way to hand work over and no new way to take it off a list.
//
//   Send to…  hands the row to a person. A notification goes through
//             notify_delegate; anything else is routed with route_record and
//             taken off this person's list with my_work_dismiss. Who sent it, to
//             whom, when and why are recorded (TCSD-0170) and the person is told.
//             This replaces "Not mine" (TCSD-0366 ruling 3), which only worked
//             when the row had a building managed by someone else.
//   Clear     takes the row off THIS person's list only: notify_triage('archive')
//             for a notification, my_work_dismiss for a derived row. Nothing is
//             marked done for anyone else — whoever else has it still has it.
//
// Undo puts the row back on this person's list through the same doors in
// reverse: notify_triage('reactivate') / my_work_restore. A Send to… stays with
// the person it went to; Undo only brings your own copy back.
// ============================================================================
import { FINANCE_SEAT, HR_SEAT, type OwningSeat } from '../../shared/role-gates';
import { supabase } from '../../shared/supabase';
import { refreshReaders } from '../../shared/save-path';
import type { WorkRow } from './home-work';

/** What Undo needs to put a row back. Kept with the Done today line. */
export interface RowUndo {
  kind: 'notification' | 'derived';
  itemType: string;
  itemId: string;
}

const isNotification = (row: WorkRow) => row.itemType === 'notification';

export function undoFor(row: WorkRow): RowUndo {
  return { kind: isNotification(row) ? 'notification' : 'derived', itemType: row.itemType, itemId: row.itemId };
}

async function dismissDerived(row: WorkRow): Promise<void> {
  const { data, error } = await supabase.rpc('my_work_dismiss', {
    p_item_type: row.itemType,
    p_item_id: row.itemId,
    p_title: row.name,
    p_deep_link_path: row.link,
  });
  if (error) throw error;
  if (!data) throw new Error('The row could not be taken off your list. Reload and try again.');
}

export async function sendTo(row: WorkRow, toEid: string, why: string): Promise<void> {
  if (!toEid) throw new Error('Pick who it should go to.');
  const reason = why.trim() ? `${why.trim()} — ${row.missing}` : `${row.missing} (sent from My Work)`;
  if (isNotification(row) && row.entityType && row.entityId) {
    const { error } = await supabase.rpc('notify_delegate', {
      p_entity_type: row.entityType,
      p_entity_id: row.entityId,
      p_to_eid: toEid,
      p_note: reason,
    });
    if (error) throw error;
  } else {
    const entityId = row.entityId ?? row.facilityId ?? '';
    if (!entityId) throw new Error('This row is not tied to a record, so it cannot be sent on. Clear it instead.');
    const { error } = await supabase.rpc('route_record', {
      p_entity_type: row.entityType ?? 'facility',
      p_entity_id: entityId,
      p_routed_to_eid: toEid,
      p_routed_to_role: null as unknown as string,
      p_reason: reason,
    });
    if (error) throw error;
    if (isNotification(row)) {
      const { error: e2 } = await supabase.rpc('notify_triage', { p_recipient_row_ids: [row.itemId], p_action: 'archive' });
      if (e2) throw e2;
    } else {
      await dismissDerived(row);
    }
  }
  refreshReaders(['record_routing', 'my_work_dismissals', 'notification_recipients']);
}

export async function clearRow(row: WorkRow): Promise<void> {
  if (isNotification(row)) {
    const { data, error } = await supabase.rpc('notify_triage', { p_recipient_row_ids: [row.itemId], p_action: 'archive' });
    if (error) throw error;
    if (!data) throw new Error('The row could not be taken off your list. Reload and try again.');
  } else {
    await dismissDerived(row);
  }
  refreshReaders(['my_work_dismissals', 'notification_recipients']);
}

export async function undoRow(u: RowUndo): Promise<void> {
  if (u.kind === 'notification') {
    const { error } = await supabase.rpc('notify_triage', { p_recipient_row_ids: [u.itemId], p_action: 'reactivate' });
    if (error) throw error;
  } else {
    const { error } = await supabase.rpc('my_work_restore', { p_item_type: u.itemType, p_item_id: u.itemId });
    if (error) throw error;
  }
  refreshReaders(['my_work_dismissals', 'notification_recipients']);
}

// ---------------------------------------------------------------------------
// Who "Send to…" is prefilled with (Ben, 2026-10-13): the building's account
// manager when there is one and it isn't you; otherwise the role that owns the
// row — finance for payment and invoicing rows, HR for onboarding and
// certificate rows; otherwise nobody, and you pick.
// ---------------------------------------------------------------------------
export interface Person {
  employee_id: string;
  name: string;
  app_role: string | null;
}

const FINANCE_ITEM_TYPES = new Set(['incentive_finance', 'project_payment_review', 'kicker_due', 'kicker_seed_review']);
const FINANCE_ENTITY_TYPES = new Set(['project', 'finance_communication', 'incentive_payout', 'invoice', 'partner_payment']);
const HR_ENTITY_TYPES = new Set(['partner_onboarding', 'employee_onboarding', 'hiring_request', 'employee', 'employee_separation']);

export function owningRole(row: WorkRow): OwningSeat | null {
  if (FINANCE_ITEM_TYPES.has(row.itemType)) return FINANCE_SEAT;
  if (row.itemType === 'open_requirement' && /^billing review/i.test(row.missing)) return FINANCE_SEAT;
  if (row.entityType && FINANCE_ENTITY_TYPES.has(row.entityType)) return FINANCE_SEAT;
  if (row.entityType && HR_ENTITY_TYPES.has(row.entityType)) return HR_SEAT;
  return null;
}

export function sendToDefault(row: WorkRow, myEid: string | null, people: Person[]): Person | null {
  if (row.amEid && row.amEid !== myEid) {
    return people.find((p) => p.employee_id === row.amEid) ?? { employee_id: row.amEid, name: row.amName ?? 'The account manager', app_role: null };
  }
  const role = owningRole(row);
  if (!role) return null;
  return people.find((p) => p.app_role === role && p.employee_id !== myEid) ?? null;
}
